/**
 * connector — `ConnectorLayer`, the MEASURED EDGE LAYER (KG-26).
 *
 * Nothing in the kit draws an edge. `Timeline` draws a straight strip between
 * items it lays out itself, `Gauge` draws arcs, and `Stack`/`Overlay` draw no
 * lines at all — so prod's discussion map (one root plate fanned by cubic
 * beziers into a column of branch rows) degraded to a row of inert chips: no
 * root, no edges, no tap-to-scroll. This is that layer.
 *
 * WHY IT OWNS BOTH ENDS. An edge is drawn between two boxes, so somebody has to
 * know where those boxes ARE, and the only honest way to know is to measure the
 * real laid-out DOM. A layer that measured its SIBLINGS would have to find them
 * by id — and blueprint ids are only in the DOM under the studio's inspect mode
 * — so the drawing and the measurement could disagree on the phone, which is
 * the exact class of bug the two-kit contract exists to prevent. Instead the
 * layer renders `from` (one node) and `to` (a template, once per bound row),
 * lays them out itself, and measures what it just placed. The endpoints are
 * then true by construction on both runtimes.
 *
 * SEALED: the edges are re-measured on every resize (a rotation must not leave
 * six lines pointing at where the rows used to be), and an edge is drawn only
 * between boxes this layer placed — there is no coordinate prop, so a blueprint
 * can never author a line into empty space.
 *
 * NO domain vocabulary: this draws N edges from one box to N boxes. What the
 * boxes say is the blueprint's business.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { gapValue, prefersReducedMotion, resetButton, toneColor } from './ui';

/** The animation prod uses: each edge draws itself in, staggered down the fan. */
const DRAW_MS = 700;
const STAGGER_MS = 70;

/**
 * One cubic bezier from the root's trailing edge to a row's leading edge.
 *
 * The control points sit at the HORIZONTAL midpoint on both ends, which is what
 * makes a fan of them read as one hand splaying out: every edge leaves the root
 * flat, bends once, and arrives at its row flat. A straight line between the
 * same two points crosses its neighbours; this never does.
 */
function edgePath({ x1, y1, x2, y2 }, curve) {
  if (curve === 'line') return `M${x1} ${y1} L${x2} ${y2}`;
  const mx = x1 + (x2 - x1) * 0.5;
  return `M${x1} ${y1} C${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
}

/**
 * ConnectorLayer — a root node, its bound branch rows, and the edges between.
 *
 * `bind` is the branch list. Props: `from` (a NODE — the root plate) · `to` (a
 * NODE template rendered per row) · `curve` (`bezier` | `line`) · `tones` (a
 * palette cycled BY INDEX, which is how prod colour-codes the fan) or `tone`
 * (one colour for every edge) · `limit` + `moreLabel` (prod caps the fan and
 * says so) · `gap` · `strokeWidth` · `opacity` · `dot` · `stagger`.
 *
 * Signal: `branchActivated {index,item}` — prod scrolls the matching detail card
 * into view, which is NOT navigation, so it lands on the interpreter's universal
 * `scrollIntoView` slot (`map.branchActivated → card.scrollIntoView`) and the
 * host is never involved. The `index` on the payload is what tells that sink
 * WHICH repetition was meant.
 */
export function ConnectorLayer({
  nodeId, props = {}, data, emit, renderTemplate,
}) {
  const rows = useMemo(() => (Array.isArray(data) ? data : (data == null ? [] : [data])), [data]);
  const limit = Number(props.limit) > 0 ? Math.trunc(Number(props.limit)) : 0;
  const shown = limit > 0 ? rows.slice(0, limit) : rows;
  const overflow = rows.length - shown.length;

  const wrapRef = useRef(null);
  const rootRef = useRef(null);
  const branchRefs = useRef([]);
  const [frame, setFrame] = useState({ w: 0, h: 0, edges: [] });

  const tones = useMemo(() => {
    if (props.tone) return [String(props.tone)];
    const list = Array.isArray(props.tones) ? props.tones.filter((t) => t != null && t !== '') : [];
    return list.length ? list.map(String) : ['accent'];
  }, [props.tone, props.tones]);

  const stroke = Number(props.strokeWidth) || 2.5;
  const curve = props.curve === 'line' ? 'line' : 'bezier';
  const dot = props.dot !== false;
  const stagger = Number.isFinite(Number(props.stagger)) ? Number(props.stagger) : STAGGER_MS;

  /**
   * MEASURE. Every endpoint is read in the wrapper's own coordinate space, so
   * the svg's viewBox and the path numbers are the same units and the drawing
   * survives any transform an enclosing page applies.
   */
  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    const root = rootRef.current;
    if (!wrap || !root) return;
    const wr = wrap.getBoundingClientRect();
    const rr = root.getBoundingClientRect();
    const x1 = rr.right - wr.left;
    const y1 = rr.top - wr.top + rr.height / 2;
    const edges = [];
    branchRefs.current.forEach((el, i) => {
      if (!el) return;
      const br = el.getBoundingClientRect();
      edges.push({
        i,
        d: edgePath({ x1, y1, x2: br.left - wr.left, y2: br.top - wr.top + br.height / 2 }, curve),
        x2: br.left - wr.left,
        y2: br.top - wr.top + br.height / 2,
        tone: tones[i % tones.length],
      });
    });
    setFrame({ w: wr.width, h: wr.height, edges });
  }, [curve, tones]);

  useLayoutEffect(() => { measure(); }, [measure, shown.length]);

  /**
   * THE DRAW-IN, as a transition rather than a keyframe: the dash starts a full
   * path-length off the end and is animated to zero one frame after mount, each
   * edge delayed by `stagger`. Prod does exactly this (`strokeDashoffset = len`
   * → rAF → `0`, `transitionDelay: i*0.07s`), and doing it the same way keeps
   * the whole component inside this file — no @keyframes to register, and no
   * second place to look when the fan does not animate.
   */
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [measure]);

  if (!shown.length) return null;
  // The interpreter supplies `renderTemplate` (item scope + signal routing);
  // `props.renderTemplate` is the documented seam a surface with no interpreter
  // around it (the studio's component browser) hands in instead — the same
  // arrangement every collection already uses.
  const layOut = renderTemplate || props.renderTemplate;
  // "This reader asked for no motion": the fan is drawn already-complete, never
  // animated in. Read at render, exactly as the rest of the kit reads it.
  const still = prefersReducedMotion();

  return (
    <div>
      <div ref={wrapRef} style={{
        position: 'relative', display: 'flex', alignItems: 'center',
        gap: gapValue(props.gap, 'var(--sp-4)'), minWidth: 0,
      }}>
        <svg
          aria-hidden
          viewBox={`0 0 ${Math.max(1, frame.w)} ${Math.max(1, frame.h)}`}
          preserveAspectRatio="none"
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            pointerEvents: 'none', overflow: 'visible', zIndex: 0,
          }}
        >
          {frame.edges.map((e) => (
            <g key={e.i}>
              <path
                data-vi-edge={e.i}
                d={e.d}
                fill="none"
                stroke={toneColor(e.tone)}
                strokeWidth={stroke}
                strokeLinecap="round"
                opacity={props.opacity == null ? 0.5 : Number(props.opacity)}
                // `pathLength` normalises the dash to 100 units, so the dash
                // numbers never need the MEASURED length — which the DOM only
                // knows after paint (and jsdom never knows at all).
                pathLength={100}
                strokeDasharray="100"
                strokeDashoffset={drawn || still ? 0 : 100}
                style={still ? undefined : {
                  transition: `stroke-dashoffset ${DRAW_MS}ms var(--ease)`,
                  transitionDelay: `${e.i * stagger}ms`,
                }}
              />
              {dot ? <circle cx={e.x2} cy={e.y2} r={3.4} fill={toneColor(e.tone)} /> : null}
            </g>
          ))}
        </svg>

        <div ref={rootRef} style={{ position: 'relative', zIndex: 1, flex: '0 0 auto', alignSelf: 'center' }}>
          {props.from && layOut ? layOut(props.from, { key: `${nodeId}__root` }) : null}
        </div>

        <div style={{
          position: 'relative', zIndex: 1, flex: 1, minWidth: 0,
          display: 'flex', flexDirection: 'column', gap: gapValue(props.gap, 'var(--sp-2)'),
        }}>
          {shown.map((row, i) => (
            <button
              key={i}
              type="button"
              data-vi-branch={i}
              ref={(el) => { branchRefs.current[i] = el; }}
              onClick={() => emit?.('branchActivated', { index: i, item: row })}
              style={{ ...resetButton, display: 'block', width: '100%', minWidth: 0 }}
            >
              {props.to && layOut
                ? layOut(props.to, {
                  item: row,
                  key: `${nodeId}__branch${i}`,
                  onSignal: (signal, payload) => emit?.(signal, { index: i, ...(payload || {}) }),
                })
                : null}
            </button>
          ))}
        </div>
      </div>

      {overflow > 0 && props.moreLabel ? (
        <div style={{
          marginTop: 'var(--sp-1)', paddingLeft: 6,
          fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--text-3)',
        }}>{`+${overflow} ${String(props.moreLabel).trim()}`}</div>
      ) : null}
    </div>
  );
}
